/**
 * Stock levels, manual adjustment, reconciliation and the movement ledger. Most routes need
 * `cost.view` in addition to the stock permission listed — the average cost and valuation fields
 * are cost-bearing, so most of this file is gated the same way Purchases is. Two exceptions,
 * both deliberately cost-free responses: `listStockQuantity` (Global Search's "do we have this
 * in stock" answer) and `findUnitLocationBySerial` (safe for the POS app to call too).
 *
 * `quantity` is always derived from the ledger server-side; nothing here ever sets it directly —
 * only a signed `quantityDelta` through an adjustment, which is itself a new ledger row, never a
 * mutation of an existing one.
 */

import { api } from '../api.js';
import { movementListTag, stockListTag, unitTag } from '../tags.js';
import type {
  AdminUnit,
  Paged,
  ReconciliationResult,
  ShopId,
  StockLevel,
  StockMovement,
  StockQuantity,
  UnitLocation,
  VariantId,
} from '../../types/api.js';

export type ListStockParams = {
  readonly shopId?: ShopId | undefined;
  readonly variantId?: VariantId | undefined;
  readonly search?: string | undefined;
  readonly onlyLowStock?: boolean | undefined;
  readonly onlyNegative?: boolean | undefined;
  readonly page?: number | undefined;
  readonly pageSize?: number | undefined;
};

export type ListMovementsParams = {
  readonly variantId?: VariantId | undefined;
  readonly shopId?: ShopId | undefined;
  readonly movementType?: string | undefined;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
  readonly page?: number | undefined;
  readonly pageSize?: number | undefined;
};

export type AdjustStockBody = {
  readonly variantId: VariantId;
  readonly shopId?: ShopId | undefined;
  readonly quantityDelta: number;
  /** Mandatory — enforced client-side too (at least 5 characters) so the round trip never fails on it alone. */
  readonly reason: string;
  /**
   * Required for a serialized variant — the server refuses a quantity-only adjustment on one
   * (`SERIALIZED_ADJUSTMENT_NEEDS_UNIT`). Exactly one unit per call: removing several means
   * several calls, one per IMEI, never a single delta bigger than 1 alongside this.
   */
  readonly inventoryUnitId?: string | undefined;
};

export type StockAdjustmentResult = {
  readonly movementId: number;
  readonly variantId: VariantId;
  readonly shopId: ShopId;
  readonly quantityDelta: number;
  readonly quantityOnHand: number;
  readonly isNegative: boolean;
};

export type OpeningStockBody = {
  readonly variantId: VariantId;
  readonly shopId?: ShopId | undefined;
  readonly quantity: number;
  readonly unitCostKes: number;
  readonly note?: string | undefined;
  /** Required, one per unit, for a serialized variant — there is no later screen to fill these in. */
  readonly serials?: readonly string[] | undefined;
};

export type OpeningStockResult = {
  readonly variantId: VariantId;
  readonly shopId: ShopId;
  readonly quantity: number;
  readonly unitCostKes: number;
  readonly quantityOnHand: number;
};

export const stockApi = api.injectEndpoints({
  endpoints: (build) => ({
    listStock: build.query<Paged<StockLevel>, ListStockParams>({
      query: (params) => ({ url: '/stock', params: { page: 1, pageSize: 25, ...params } }),
      providesTags: [stockListTag()],
    }),

    /** No `cost.view` needed — see `StockQuantity`'s own doc comment. Global Search uses this. */
    listStockQuantity: build.query<
      Paged<StockQuantity>,
      { shopId?: ShopId | undefined; search?: string | undefined; page?: number | undefined; pageSize?: number | undefined }
    >({
      query: (params) => ({ url: '/stock/quantity', params: { page: 1, pageSize: 25, ...params } }),
      providesTags: [stockListTag()],
    }),

    listLowStock: build.query<StockLevel[], { shopId?: ShopId | undefined; limit?: number | undefined }>({
      query: (params) => ({ url: '/stock/low', params }),
      providesTags: [stockListTag()],
    }),

    adjustStock: build.mutation<StockAdjustmentResult, AdjustStockBody>({
      query: (body) => ({ url: '/stock/adjustments', method: 'POST', body }),
      invalidatesTags: [stockListTag(), movementListTag()],
    }),

    recordOpeningStock: build.mutation<OpeningStockResult, OpeningStockBody>({
      query: (body) => ({ url: '/stock/opening-balance', method: 'POST', body }),
      invalidatesTags: [stockListTag(), movementListTag()],
    }),

    reconcileStock: build.mutation<ReconciliationResult, { repair: boolean }>({
      query: (body) => ({ url: '/stock/reconcile', method: 'POST', body }),
      invalidatesTags: (result) => (result !== undefined && result.repaired > 0 ? [stockListTag()] : []),
    }),

    listMovements: build.query<Paged<StockMovement>, ListMovementsParams>({
      query: (params) => ({ url: '/movements', params: { page: 1, pageSize: 25, ...params } }),
      providesTags: [movementListTag()],
    }),

    /**
     * Every serialized unit in stock for one variant — the dashboard's IMEI list.
     *
     * `status: 'InStock'`, matching `UnitStatus.InStock`'s own C# member name exactly — the
     * server parses this with `Enum.TryParse`, which does not strip underscores. `'in_stock'`
     * looked plausible and was wrong: it failed to parse server-side, the server has always
     * (correctly) returned a validation error for it, and this screen rendered that identically
     * to a genuinely empty result — "no units" when the real answer was "the filter was
     * rejected." Real IMEIs that existed never showed. See `useListUnitsQuery`'s error handling
     * in `SerialUnitsTable` for the other half of this fix.
     */
    listUnits: build.query<
      Paged<AdminUnit>,
      {
        shopId: ShopId;
        variantId: VariantId;
        /** On sale by default; `ReturnedFaulty` for the handsets marked faulty. */
        status?: 'InStock' | 'ReturnedFaulty' | undefined;
      }
    >({
      query: ({ shopId, variantId, status }) => ({
        url: '/units',
        params: { shopId, variantId, status: status ?? 'InStock', page: 1, pageSize: 200 },
      }),
      providesTags: (_result, _error, { shopId, variantId }) => [unitTag(shopId, variantId)],
    }),

    /**
     * Every unit a purchase brought in, regardless of current status — the purchase detail
     * screen's actual IMEIs, not just how many were captured. No status filter, deliberately: a
     * unit already sold by the time someone looks at this purchase is still part of its history.
     */
    listUnitsByPurchase: build.query<Paged<AdminUnit>, { purchaseId: string }>({
      query: ({ purchaseId }) => ({ url: '/units', params: { purchaseId, page: 1, pageSize: 500 } }),
    }),

    /**
     * Where is this handset? Business-wide, not shop-scoped — see the endpoint's own summary on
     * the server. Built for the moment a customer returns with a phone, or asks where one is.
     */
    findUnitBySerial: build.query<AdminUnit, string>({
      query: (serial) => `/units/by-serial/${encodeURIComponent(serial)}`,
    }),

    /**
     * Units whose IMEI or serial contains the typed digits, at least four, from every shop — the
     * whole number first, then numbers ending in them. "Find a unit" uses this, so the last few
     * digits read off a box are enough.
     */
    searchUnitsBySerial: build.query<AdminUnit[], string>({
      query: (serial) => ({ url: '/units/search', params: { serial } }),
    }),

    /**
     * Same lookup, no cost — for a caller who holds `unit.view` but not `cost.view`. This is
     * the one this file's endpoints let the POS app call.
     */
    findUnitLocationBySerial: build.query<UnitLocation, string>({
      query: (serial) => `/units/by-serial/${encodeURIComponent(serial)}/location`,
    }),
  }),
});

export const {
  useListStockQuery,
  useListStockQuantityQuery,
  useListLowStockQuery,
  useAdjustStockMutation,
  useRecordOpeningStockMutation,
  useReconcileStockMutation,
  useListMovementsQuery,
  useListUnitsQuery,
  useListUnitsByPurchaseQuery,
  useLazyFindUnitBySerialQuery,
  useLazySearchUnitsBySerialQuery,
  useLazyFindUnitLocationBySerialQuery,
} = stockApi;
